import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export default function PushToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const isSup = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(isSup);
    if (!isSup) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js') || await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Autorise les notifications dans ton navigateur'); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      const { data } = await api.get('/push/vapid-public-key');
      if (!data.public_key) { toast.error('Push non configuré côté serveur'); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const payload = sub.toJSON();
      await api.post('/push/subscribe', { endpoint: payload.endpoint, keys: payload.keys });
      setEnabled(true);
      toast.success('Notifications activées 🔔');
    } catch (e) {
      console.error(e);
      toast.error("Impossible d'activer les notifications");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        const p = sub.toJSON();
        await api.post('/push/unsubscribe', { endpoint: p.endpoint, keys: p.keys });
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success('Notifications désactivées');
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const testPush = async () => {
    try {
      await api.post('/push/test');
      toast.success('Test envoyé — regarde ta notification !');
    } catch { toast.error('Erreur'); }
  };

  if (!supported) {
    return (
      <div className="text-stone-500 text-sm">Les notifications ne sont pas supportées sur ce navigateur.</div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="push-toggle">
      {enabled ? (
        <>
          <button data-testid="push-disable" onClick={disable} disabled={busy} className="btn-ghost inline-flex items-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <BellOff size={14} />}
            Désactiver les notifications
          </button>
          <button data-testid="push-test" onClick={testPush} className="btn-ghost inline-flex items-center gap-2 text-xs">
            Tester
          </button>
        </>
      ) : (
        <button data-testid="push-enable" onClick={enable} disabled={busy} className="btn-primary inline-flex items-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Bell size={14} />}
          Activer les notifications
        </button>
      )}
    </div>
  );
}
